import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { bulkImportMembers, type BulkMemberInput, type BulkImportResult } from '../../services/authApi'

interface BulkImportTabProps {
  rdId: string
}

type Step = 'input' | 'preview' | 'result'

export function BulkImportTab({ rdId }: BulkImportTabProps) {
  const [step, setStep] = useState<Step>('input')
  const [rawInput, setRawInput] = useState('')
  const [members, setMembers] = useState<BulkMemberInput[]>([])
  const [parseError, setParseError] = useState('')
  const [result, setResult] = useState<BulkImportResult | null>(null)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState('')

  function handleParse() {
    setParseError('')
    const trimmed = rawInput.trim()
    if (!trimmed) {
      setParseError('Input is empty')
      return
    }

    // Try JSON first
    try {
      const parsed = JSON.parse(trimmed)
      const arr = Array.isArray(parsed) ? parsed : parsed.members
      if (!Array.isArray(arr)) throw new Error('Expected array')

      const validated: BulkMemberInput[] = arr.map((m: Record<string, string>, i: number) => {
        if (!m.userId) throw new Error(`Row ${i + 1}: missing userId`)
        if (!m.email) throw new Error(`Row ${i + 1}: missing email`)
        if (!m.role) throw new Error(`Row ${i + 1}: missing role`)
        return {
          userId: m.userId,
          email: m.email,
          name: m.name || '',
          role: m.role,
        }
      })

      setMembers(validated)
      setStep('preview')
      return
    } catch {
      // Not JSON — try CSV
    }

    // Try CSV (userId,email,name,role)
    try {
      const lines = trimmed.split('\n').filter(l => l.trim())
      const firstLine = lines[0]!.toLowerCase()
      const hasHeader = firstLine.includes('userid') || firstLine.includes('email')
      const dataLines = hasHeader ? lines.slice(1) : lines

      const validated: BulkMemberInput[] = dataLines.map((line, i) => {
        const parts = line.split(',').map(p => p.trim().replace(/^["']|["']$/g, ''))
        if (parts.length < 3) throw new Error(`Row ${i + 1}: expected at least 3 columns (userId, email, name, role)`)
        return {
          userId: parts[0]!,
          email: parts[1]!,
          name: parts[2] || '',
          role: parts[3] || 'analyst',
        }
      })

      setMembers(validated)
      setStep('preview')
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Failed to parse input')
    }
  }

  async function handleImport() {
    setError('')
    setImporting(true)
    try {
      const importResult = await bulkImportMembers(rdId, members)
      setResult(importResult)
      setStep('result')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  function handleReset() {
    setStep('input')
    setRawInput('')
    setMembers([])
    setResult(null)
    setError('')
    setParseError('')
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {step === 'input' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste JSON or CSV to bulk import users into this research domain. Each user needs a userId (Cognito sub), email, name, and role.
          </Typography>

          <Paper sx={{ border: '1px solid', borderColor: 'divider', p: 2, mb: 2 }} elevation={0}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              JSON format:
            </Typography>
            <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary', mb: 2 }}>
              {`[{"userId":"abc-123","email":"leah@acme.com","name":"Leah Kim","role":"analyst"}]`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              CSV format (header optional):
            </Typography>
            <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
              {`userId,email,name,role\nabc-123,leah@acme.com,Leah Kim,analyst`}
            </Typography>
          </Paper>

          {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}

          <TextField
            fullWidth multiline rows={10}
            placeholder="Paste JSON array or CSV here..."
            value={rawInput}
            onChange={e => setRawInput(e.target.value)}
            sx={{ mb: 2, fontFamily: 'monospace' }}
          />

          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={handleParse} disabled={!rawInput.trim()}>
            Parse & Preview
          </Button>
        </Box>
      )}

      {step === 'preview' && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            {members.length} user{members.length !== 1 ? 's' : ''} ready to import. Review below and click Import.
          </Alert>

          <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.slice(0, 50).map((m, i) => (
                  <TableRow key={i} hover>
                    <TableCell><code style={{ fontSize: '0.75rem' }}>{m.userId.slice(0, 20)}{m.userId.length > 20 ? '...' : ''}</code></TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>{m.name || '—'}</TableCell>
                    <TableCell><Chip label={m.role} size="small" variant="outlined" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {members.length > 50 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Showing first 50 of {members.length} rows
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => setStep('input')}>Back</Button>
            <Button variant="contained" onClick={handleImport} disabled={importing}>
              {importing ? 'Importing...' : `Import ${members.length} Users`}
            </Button>
          </Box>
        </Box>
      )}

      {step === 'result' && result && (
        <Box>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Import complete: {result.imported} imported, {result.skipped} skipped
          </Alert>

          {result.errors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {result.errors.length} error{result.errors.length !== 1 ? 's' : ''}:
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {result.errors.map((e, i) => (
                  <li key={i}><code>{e.userId}</code>: {e.error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <Button variant="outlined" onClick={handleReset}>Import More</Button>
        </Box>
      )}
    </Box>
  )
}
